/** Maps every hub source to a HubResult with plain second-line words (spec §4). */
import type { DurableConversation } from "@/api/conversations";
import type { MemoryThing } from "@/api/memory-focus";
import type { Project } from "@/api/spaces";
import type {
  WorkspaceAgent,
  WorkspaceFrame,
  WorkspaceTask,
} from "@/api/workspace-search";
import { conversationIsRunning } from "@/components/panels/conversation-status";
import {
  SETTINGS_PAGES,
  type SettingsPageDef,
} from "@/components/settings/settings-navigation";
import { conversationDisplayName } from "@/lib/conversation-display";
import { frameTypeLabel } from "@/components/shapes/shared/frame-type-label";
import { formatRelativeTime, translate } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { kindName, thingDisplayName } from "@/lib/memory-thing-kinds";
import type { RecentOpened } from "@/lib/recent-opened";
import type { HubResult } from "./hub-types";

export function joinSecondLine(
  ...parts: Array<string | null | undefined>
): string {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");
}

/** "sticky_note" → "Sticky note". */
function timeOf(iso: string | null | undefined): number {
  const time = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(time) ? time : 0;
}

export function chatResult(chat: DurableConversation): HubResult {
  const running = conversationIsRunning(chat);
  return {
    kind: "chat",
    id: chat.id,
    title: conversationDisplayName(chat),
    secondLine: joinSecondLine(
      chat.project_name,
      running
        ? translate(panelsSearchMessages, "runningNow")
        : formatRelativeTime(chat.activity_at, { style: "narrow" }),
    ),
    time: timeOf(chat.activity_at),
    projectId: chat.project_id,
    projectName: chat.project_name,
    chat,
    running,
    archived: Boolean(chat.archived_at),
  };
}

export function taskResult(task: WorkspaceTask): HubResult {
  return {
    kind: "task",
    id: task.id,
    title: task.title,
    secondLine: joinSecondLine(
      task.ticket_display,
      task.project_name,
      task.assignee_name || translate(panelsSearchMessages, "unassigned"),
      task.status === "done"
        ? formatRelativeTime(task.updated_at, { style: "narrow" })
        : null,
    ),
    time: timeOf(task.updated_at),
    projectId: task.project_id,
    projectName: task.project_name,
    status: task.status,
    tone: task.status === "blocked" || task.needs_attention ? "question" : null,
  };
}

export function frameResult(frame: WorkspaceFrame): HubResult {
  const type = frameTypeLabel(frame.frame_type);
  return {
    kind: "frame",
    id: frame.id,
    title: frame.title || type,
    secondLine: joinSecondLine(type, frame.project_name),
    time: timeOf(frame.updated_at),
    projectId: frame.project_id,
    projectName: frame.project_name,
    frameType: frame.frame_type,
  };
}

/** A server-matched thing; search results have no recency timestamp. */
export function memoryResult(thing: MemoryThing & { id: string }): HubResult {
  return {
    kind: "memory",
    id: thing.id,
    title: thingDisplayName(thing),
    secondLine: kindName(thing.kind),
    time: 0,
    projectId: null,
    projectName: null,
  };
}

export function projectResult(project: Project): HubResult {
  return {
    kind: "project",
    id: project.id,
    title: project.name,
    secondLine: "",
    time: timeOf(project.updated_at),
    projectId: null,
    projectName: null,
    color: project.color,
    isHome: Boolean(project.is_home),
  };
}

export function agentResult(agent: WorkspaceAgent): HubResult {
  return {
    kind: "agent",
    id: agent.id,
    title: agent.name,
    secondLine: agent.project_name,
    time: timeOf(agent.updated_at ?? ""),
    projectId: agent.project_id,
    projectName: agent.project_name,
    templateId: agent.template_id,
  };
}

export function settingResult(page: SettingsPageDef): HubResult {
  const parent =
    page.parent && page.parent !== "root"
      ? SETTINGS_PAGES[page.parent]
      : undefined;
  return {
    kind: "setting",
    id: page.id,
    title: page.title,
    secondLine: parent?.title ?? translate(panelsSearchMessages, "settings"),
    time: 0,
    projectId: null,
    projectName: null,
    page: page.id,
    keywords: page.keywords ?? [],
  };
}

export function recentResult(entry: RecentOpened): HubResult {
  const base = {
    id: entry.id,
    title: entry.title,
    time: entry.openedAt,
    projectId: entry.projectId,
    projectName: entry.projectName,
  };
  const opened = formatRelativeTime(entry.openedAt, { style: "narrow" });
  switch (entry.kind) {
    case "chat":
      return {
        ...base,
        kind: "chat",
        secondLine: joinSecondLine(entry.projectName, opened),
        chat: null,
        running: false,
        archived: false,
      };
    case "frame":
      return {
        ...base,
        kind: "frame",
        secondLine: joinSecondLine(
          frameTypeLabel(entry.frameType ?? ""),
          entry.projectName,
        ),
        frameType: entry.frameType ?? "",
      };
    case "task":
      return {
        ...base,
        kind: "task",
        secondLine: joinSecondLine(entry.projectName, opened),
        status: null,
        tone: null,
      };
  }
}
